import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Modal, Switch } from "antd";
import { Body } from "../../../components/main-body/body";
import useNotification from "../../../components/notification/notification";
import ApiSystemSettingMcp from "../../../api/system-setting-mcp";
import PhaseSelect, { WIDEN_TO_DRAFTS, isWider } from "./phase-select";

import "../authentication/authentication.css";
import "./ai-connectors.css";

const TURN_ON = {
  title: "Turn on AI connectors?",
  content:
    "Users who may connect will be able to give their Claude or ChatGPT assistant read access to "
    + "this organization's content. What the assistant reads is sent to Anthropic or OpenAI.",
  okText: "Turn on",
};

const TURN_OFF = {
  title: "Stop all AI calls for the organization?",
  content: "Connections are kept and work again when you turn this back on.",
  okText: "Stop",
  okButtonProps: { danger: true },
};

/**
 * LF360-2100 — the organization's switch for AI connectors (MCP), for System
 * admins.
 *
 * Off by default. Off is also the kill switch: every AI call and every new
 * connection is refused from the next request, and existing connections come
 * back when it is on again. The server address shown is the one users paste
 * into Claude or ChatGPT.
 */
const AiConnectors = () => {
  const { openNotification } = useNotification();
  const notifyRef = useRef(openNotification);
  notifyRef.current = openNotification;

  const [modal, modalContext] = Modal.useModal();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    ApiSystemSettingMcp.get()
      .then((res) => alive && setSettings(res.data.mcp))
      .catch(() => alive && notifyRef.current({ type: "error", message: <p>Failed to load AI connector settings</p> }));

    return () => {
      alive = false;
    };
  }, []);

  // `changes`: only what is being changed; the server leaves the rest.
  const save = (changes) => {
    setSaving(true);
    return ApiSystemSettingMcp.update(changes)
      .then((res) => {
        setSettings(res.data.mcp);
        notifyRef.current({ type: "success", message: <p>AI connector settings saved</p> });
      })
      .catch(() => notifyRef.current({ type: "error", message: <p>Failed to save AI connector settings</p> }))
      .finally(() => setSaving(false));
  };

  const toggle = (mcpEnabled) => modal.confirm({
    ...(mcpEnabled ? TURN_ON : TURN_OFF),
    cancelText: "Cancel",
    onOk: () => save({ mcpEnabled }),
  });

  // Widening asks first; narrowing is saved at once.
  const changePhase = (maxPhase) => {
    if (!isWider(settings.maxPhase, maxPhase)) {return save({ maxPhase });}
    return modal.confirm({ ...WIDEN_TO_DRAFTS, cancelText: "Cancel", onOk: () => save({ maxPhase }) });
  };

  const copy = () => navigator.clipboard?.writeText(settings.serverUrl)
    .then(() => notifyRef.current({ type: "success", message: <p>Copied</p> }))
    .catch(() => {});

  return (
    <Body scroll header={{ title: "AI connectors (MCP)" }}>
      {modalContext}
      <div className="auth-portal-settings">
        <div className="profile_form-container">
          <div className="authps-section authps-pad">
            <div className="authps-row">
              <div className="authps-label">AI connectors enabled</div>
              <div className="authps-value">
                <Switch
                  aria-label="AI connectors enabled"
                  checked={Boolean(settings?.mcpEnabled)}
                  disabled={!settings}
                  loading={saving}
                  onChange={toggle}
                />
              </div>
            </div>
            <p className="ai-connectors__text">
              Lets users connect their own Claude or ChatGPT assistant to LifeFit360. An assistant sees only what
              its user can see in the project chosen on the consent screen, and changes nothing unless the maximum
              phase below allows drafts.
            </p>
            {/* Two switches with nearly one name: say where the second one is. */}
            <p className="ai-connectors__text" data-testid="ai-connectors-project-hint">
              Each project is off until it is allowed there too: Project settings →{" "}
              <Link to="../project-ai-connectors" relative="path">AI connectors</Link> of the open project, where its connections
              are listed as well.
            </p>
          </div>

          <div className="authps-divider" />

          <div className="authps-section authps-pad">
            <div className="authps-row">
              <div className="authps-label">Maximum phase</div>
              <div className="authps-value">
                <PhaseSelect
                  label="Maximum phase"
                  value={settings?.maxPhase}
                  phases={settings?.phases}
                  disabled={!settings}
                  loading={saving}
                  onChange={changePhase}
                />
              </div>
            </div>
            <p className="ai-connectors__text">
              The most any project may allow. Each project chooses its own, up to this one.
            </p>
          </div>

          <div className="authps-divider" />

          <div className="authps-section authps-pad">
            <div className="authps-label">Server address</div>
            <div className="ai-connectors__url">
              <code data-testid="ai-connectors-server-url">{settings?.serverUrl || ""}</code>
              <Button size="small" onClick={copy} disabled={!settings}>Copy</Button>
            </div>
            <p className="ai-connectors__text">
              Users add a connector with this address in Claude (custom connector) or ChatGPT (Developer mode),
              and see their connections on <Link to="/oauth/connections">My AI connections</Link>.
            </p>
          </div>
        </div>
      </div>
    </Body>
  );
};

export default AiConnectors;
